import { MONTHS, WEEKDAYS, navBtn, TYPE_COLORS } from './calendar-page.support';
import { str } from '../../../platform/api/utils';

interface GridProps {
  year: number;
  month: number;
  daysInMonth: number;
  startOffset: number;
  dayEvents: Record<number, Record<string, unknown>[]>;
  isLoading: boolean;
  today: Date;
  selectedDay: number | null;
  onChangeMonth: (delta: number) => void;
  onSelectDay: (day: number) => void;
}
export function CalendarGrid({
  year,
  month,
  daysInMonth,
  startOffset,
  dayEvents,
  isLoading,
  today,
  selectedDay,
  onChangeMonth,
  onSelectDay,
}: GridProps) {
  return (
    <div style={{ flex: 1 }}>
      {/* Month nav */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}
      >
        <button
          onClick={() => {
            onChangeMonth(-1);
          }}
          style={navBtn}
        >
          ← {MONTHS[(month + 11) % 12]}
        </button>
        <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--ink)' }}>
          {MONTHS[month]} / {year}
        </span>
        <button
          onClick={() => {
            onChangeMonth(1);
          }}
          style={navBtn}
        >
          {MONTHS[(month + 1) % 12]} →
        </button>
      </div>

      {/* Weekday headers */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          borderBottom: '2px solid var(--line)',
          paddingBottom: '0.5rem',
          marginBottom: '0.25rem',
        }}
      >
        {WEEKDAYS.map((wd) => (
          <div
            key={wd}
            style={{
              textAlign: 'center',
              fontSize: '0.7rem',
              fontWeight: 600,
              color: 'var(--faint)',
              textTransform: 'uppercase',
            }}
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Days grid */}
      {isLoading ? (
        <div
          role="status"
          aria-label="Carregando calendário"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 1,
            background: 'var(--line-soft)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: 35 }, (_, i) => (
            <div key={i} style={{ background: '#fff', height: 80 }} />
          ))}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 1,
            background: 'var(--line-soft)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: startOffset }, (_, i) => (
            <div key={`pad-${String(i)}`} style={{ background: 'var(--paper)', height: 80 }} />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dayEvts = dayEvents[day] ?? [];
            const isToday =
              today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
            const isSelected = selectedDay === day;

            return (
              <button
                type="button"
                aria-label={`Dia ${String(day)}: ${String(dayEvts.length)} atividades`}
                aria-pressed={isSelected}
                key={day}
                onClick={() => {
                  onSelectDay(day);
                }}
                style={{
                  background: isSelected
                    ? 'var(--info-soft)'
                    : isToday
                      ? 'var(--warning-soft)'
                      : '#fff',
                  border: 0,
                  minWidth: 0,
                  textAlign: 'left',
                  height: 80,
                  padding: '0.25rem',
                  cursor: 'pointer',
                  borderLeft: isSelected ? '3px solid var(--green)' : '3px solid transparent',
                  overflow: 'hidden',
                  transition: 'background 0.1s',
                }}
              >
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: isToday ? 700 : 500,
                    color: isToday ? 'var(--green)' : 'var(--muted)',
                    marginBottom: '0.15rem',
                  }}
                >
                  {day}
                </div>
                {dayEvts.slice(0, 2).map((ev) => (
                  <div
                    key={str(ev.id)}
                    title={str(ev.title)}
                    style={{
                      fontSize: '0.65rem',
                      padding: '0.05rem 0.3rem',
                      borderRadius: 6,
                      background: TYPE_COLORS[str(ev.type)] ?? 'var(--line-soft)',
                      color: 'var(--ink)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      marginBottom: 1,
                    }}
                  >
                    {str(ev.title)}
                  </div>
                ))}
                {dayEvts.length > 2 && (
                  <div style={{ fontSize: '0.6rem', color: 'var(--faint)' }}>
                    +{dayEvts.length - 2} mais
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
